import {
  Image as ChakraUIImage,
  Portal,
  type ImageProps as ChakraUIImageProps
} from "@chakra-ui/react";

import { MouseEvent, ReactNode, useCallback, useState } from "react";

import { Box } from "../internal/exports";
import { Dialog } from "../chakra-slots";

export interface ImageProps extends ChakraUIImageProps {
  magnify?: boolean;
  fallback?: ReactNode;
}

function ImageWithFallback({
  fallback,
  onError,
  ...rest
}: ChakraUIImageProps & { fallback?: ReactNode }) {
  const [hasError, setHasError] = useState(false);

  if (hasError && fallback) {
    return <>{fallback}</>;
  }

  return (
    <ChakraUIImage
      {...rest}
      onError={(event) => {
        setHasError(true);
        onError?.(event);
      }}
    />
  );
}

export function Image({ magnify, fallback, ...rest }: ImageProps) {
  const [open, setOpen] = useState(false);

  const handleClick = (e: MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    setOpen(true);
  };

  const handleOpenChange = useCallback((details: { open: boolean }) => {
    if (!details.open) {
      setOpen(false);
    }
  }, []);

  if (magnify) {
    return (
      <>
        <Box
          mr={2}
          pt={1}
          as="span"
          opacity={1}
          minW="100px"
          cursor="zoom-in"
          onClick={handleClick}
          _hover={{ opacity: 0.7 }}
        >
          <ImageWithFallback
            borderRadius="lg"
            objectFit="cover"
            fallback={fallback}
            {...rest}
          />
        </Box>
        <Dialog.Root size="xl" open={open} onOpenChange={handleOpenChange}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content width="auto">
                <Dialog.Body p={0}>
                  <ImageWithFallback
                    width="auto"
                    height="auto"
                    objectFit="cover"
                    fallback={fallback}
                    {...rest}
                  />
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      </>
    );
  }

  return (
    <ImageWithFallback
      borderRadius="lg"
      objectFit="cover"
      fallback={fallback}
      {...rest}
    />
  );
}
